import { UserRegisterDto, UserInfo } from '@interfaces/auth';
import { Box, IconButton } from '@mui/material';
import { Typography } from '@vibTheme/components/_base/Typography';
import { useState } from 'react';
import { Link } from '@vibTheme/components/_base/Link';
import { ChoosePasswordStep } from '@components/Register/ChoosePassword';
import { DocumentInfoStep } from '@components/Register/DocumentInfo';
import { AudienceInfoStep } from '@components/Register/AudienceInfo';
import { UserInfoStep } from '@components/Register/UserInfo';
import { Layouts } from '@interfaces/layouts';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import useToastContext from '@contexts/ToastContext/hook';
import { useRouter } from 'next/router';
import { resolveInternalCallback } from '@utils/resolveInternalCallback';
import { signIn } from 'next-auth/react';
import { clearSelectedGroups } from '@repository/selectedGroups';
import { clearSelectedOffers } from '@repository/selectedOffers';
import { clearSelectedFilters } from '@repository/selectedFilters';
import { clearWelcomeDialogPostponement } from '@repository/welcomeDialog';
import { NextPage, NextPageContext } from 'next';
import { RegisterStepper } from '@components/Register/RegisterStepper';
import { ClientSideOnly } from '@components/ClientSideOnly';
import { register } from '@api/auth';
import {
  getRefFromLocalStorage,
  removeRefFromLocalStorage,
} from '@utils/captureRef';
import {
  getAttributionFromLocalStorage,
  removeAttributionFromLocalStorage,
} from '@utils/captureAttribution';
import { getFirstTouchFromLocalStorage } from '@utils/captureFirstTouch';
import { getGaClientId } from '@utils/gaClientId';
import { getMetaBrowserSignals } from '@utils/metaBrowserSignals';
import { PageTypes } from '@interfaces/pageType';
import { GTM_EVENTS, pushGtmEvent } from '@utils/gtmEvent';
import { pushMetaIdentity } from '@utils/metaIdentity';
import {
  META_LEAD_EVENT_ID_KEY,
  newMetaLeadEventId,
} from '@utils/metaLeadEvent';
import { PlatformUser } from '@interfaces/profile';

export interface StepProps {
  queryParams?: UserInfo;
  stateStorage: Partial<UserRegisterDto> | undefined;
  publish: (data: Partial<UserRegisterDto>) => void;
  isLoading?: boolean;
}

type PageProps = Pick<StepProps, 'queryParams'> & NextPage;

export default function Register({ queryParams }: PageProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [userData, setUserData] = useState<Partial<UserRegisterDto>>();
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToastContext();
  const router = useRouter();

  /*
    Um evento por passo CONCLUÍDO, e não por clique no botão.

    Esta função é chamada pelo `onSubmit` de cada passo, que o `react-hook-form` só executa depois
    de a validação passar. O gatilho antigo — Form Submit Listener no GTM — reagia ao submit NATIVO
    do `<form>`, que acontece a cada clique, inclusive quando a validação rejeita: daí o passo 1
    disparar 2,23 vezes por pessoa contra ~1,05 dos outros dois.

    Ver `utils/gtmEvent.ts` para por que isto empurra ao `dataLayer` em vez de enviar pelo `gtag`.
  */
  const PASSO_POR_INDICE = [
    GTM_EVENTS.passoUsuario,
    GTM_EVENTS.passoDocumento,
    GTM_EVENTS.passoAudiencia,
    GTM_EVENTS.passoSenha,
  ] as const;

  const handleStepSubmit = (data: Partial<UserRegisterDto>) => {
    const evento = PASSO_POR_INDICE[currentStep];
    if (evento) pushGtmEvent(evento);

    if (currentStep === steps.length - 1) {
      requestUserRegister(data as UserRegisterDto);
    } else {
      setUserData(data);
      goToNextStep();
    }
  };

  /*
    Uma navegação que falhe depois do cadastro não pode virar exceção nem passar batido: a conta já existe, e
    qualquer `catch` acima diria que o cadastro falhou. O router rejeita quando dá erro e resolve `false`
    quando a transição é cancelada — nos dois casos, recarregar na mão é o caminho que sempre chega.
  */
  const goTo = async (path: string) => {
    const navigated = await router.push(path).catch(() => false);
    if (!navigated) window.location.assign(path);
  };

  // Só o `register` neste `try`: depois dele a conta existe, e nada que falhe adiante é erro de cadastro.
  const requestUserRegister = async (userRegisterDto: UserRegisterDto) => {
    setIsLoading(true);
    let criado: PlatformUser | undefined;
    // Um por tentativa: só o da tentativa que criou a conta chega ao banco e ao `dataLayer`.
    const metaLeadEventId = newMetaLeadEventId();
    try {
      const refValue = getRefFromLocalStorage();
      const urlParams = new URLSearchParams(window.location.search);
      const rct = urlParams.get('rct');
      /*
        A origem guardada na entrada do app (`utils/captureAttribution`). Espalhada e não montada
        campo a campo: o que não foi visto simplesmente não vai no corpo, e ausência é o caso comum
        — quem chega por tráfego direto ou orgânico não traz nenhum deles, e isso é o balde "sem
        origem", não erro.
      */
      const attribution = getAttributionFromLocalStorage();
      criado = await register({
        ...userRegisterDto,
        ...attribution,
        /*
          Primeiro toque (`utils/captureFirstTouch`) e o client id do GA4 (`utils/gaClientId`): o
          que explica quem chegou SEM parâmetro de campanha — orgânico, direto, link em bio.
        */
        ...getFirstTouchFromLocalStorage(),
        gaClientId: getGaClientId(),
        // Cookies do pixel do Meta, para a API de Conversões (`utils/metaBrowserSignals`).
        ...getMetaBrowserSignals(),
        referrerCode: refValue,
        registerCommissionTracker: rct,
        metaLeadEventId,
      });
    } catch (err) {
      toast.handleError(err, 'Ocorreu um erro ao criar o cadastro.');
      setIsLoading(false);
      return;
    }

    /*
      A conta existe. Este é o evento que deveria ser a conversão do Google Ads — hoje a ação
      principal da conta é o PRIMEIRO dos três passos, que conta quem começou como se tivesse
      terminado. Ver `utils/gtmEvent.ts`.
    */
    /*
      O `external_id` do Meta ANTES do `cadastro_criado`: a tag de correspondência avançada do GTM
      precisa reiniciar o pixel com ele antes de a tag do `Lead` disparar, senão o `Lead` sai sem o
      id que liga o navegador aos eventos do servidor. Ver `utils/metaIdentity.ts`.
    */
    await pushMetaIdentity(criado?.id);
    // O mesmo `eventID` que foi no corpo: é o que o Meta usa para contar o `Lead` do navegador e o do
    // servidor como um só. Ver `utils/metaLeadEvent.ts`.
    pushGtmEvent(GTM_EVENTS.cadastroCriado, {
      [META_LEAD_EVENT_ID_KEY]: metaLeadEventId,
    });

    removeRefFromLocalStorage();
    /*
      Só depois do cadastro criado, como o `referrerCode`: um erro no `register` mantém a origem
      guardada para a próxima tentativa. Limpar antes atribuiria "sem origem" a quem só errou a
      senha na primeira vez.
    */
    removeAttributionFromLocalStorage();
    toast.setContent('Cadastro realizado com sucesso!');
    await signInAfterRegister(userRegisterDto);
  };

  /*
    Autentica com o `userRegisterDto` porque `register` devolve o usuário criado, mas não a senha.

    Só a autenticação fica no `try`. Uma navegação que falhe depois dela cairia no fallback com a sessão já
    aberta, e `/auth/signIn` não renderiza para quem está autenticado: o `PublicPageWrapper` devolve o overlay
    e a pessoa fica presa no spinner.

    `isLoading` não volta a `false` no caminho feliz: o botão segue travado até a próxima tela montar.
  */
  /**
   * A URL de login com o destino junto, quando há um.
   *
   * Usada nos DOIS caminhos que mandam para o login: o link "já possui cadastro", clicado por quem
   * percebe no meio do cadastro que já tem conta, e o fallback de quando o login automático falha.
   * O primeiro é o que estava faltando — quem chega ao cadastro por um convite e clica ali perdia o
   * destino, e terminava em `/` com o convite pendente e o cookie de capacidade correndo.
   */
  const urlDeLogin = () => {
    const destino = destinoDoCadastro();

    return destino
      ? `/auth/signIn?callbackUrl=${encodeURIComponent(destino)}`
      : '/auth/signIn';
  };

  /** O destino validado, ou `null`. Usado nos dois desfechos do cadastro. */
  const destinoDoCadastro = () =>
    resolveInternalCallback(
      router.query.callbackUrl,
      typeof window === 'undefined' ? '' : window.location.origin,
    );

  const signInAfterRegister = async ({
    document,
    password,
  }: UserRegisterDto) => {
    try {
      clearSelectedGroups();
      clearSelectedOffers();
      clearSelectedFilters();

      const response = await signIn('credentials', {
        document,
        password,
        redirect: false,
      });
      if (response?.error) throw new Error(response.error);
      clearWelcomeDialogPostponement();
    } catch (err) {
      toast.setContent(
        'Sua conta foi criada, mas não conseguimos entrar automaticamente. Faça login para continuar.',
        'warning',
      );
      setIsLoading(false);
      /*
        O destino sobrevive ao caminho de exceção também. A conta foi criada e o login automático
        falhou; sem levar o `callbackUrl`, um login manual bem-sucedido termina em `/` — com o
        convite ainda pendente e o cookie de capacidade correndo.
      */
      await goTo(urlDeLogin());
      return;
    }

    /*
      Comunidades, e não a home: é onde o diálogo de boas-vindas se abre sozinho para quem não tem
      nenhuma.

      Salvo quando o cadastro veio de um destino específico. O convite de colaborador é o caso que
      obriga isto: a pessoa clica no link do e-mail, cai numa página privada sem conta, passa por
      signIn e cadastro — e sem honrar o `callbackUrl` ela termina em `/products` com o cookie de
      capacidade correndo e nenhuma indicação de que havia um convite a aceitar.

      `resolveInternalCallback` faz as duas coisas que uma checagem de prefixo não faz: aceita a
      forma ABSOLUTA de mesma origem, que é a que o `signIn()` produz por padrão, e recusa redirect
      aberto resolvendo contra a origem em vez de comparar prefixos.
    */
    await goTo(destinoDoCadastro() ?? '/products');
  };

  const steps = [
    <UserInfoStep
      queryParams={queryParams}
      stateStorage={userData}
      publish={handleStepSubmit}
    />,
    <DocumentInfoStep stateStorage={userData} publish={handleStepSubmit} />,
    <AudienceInfoStep stateStorage={userData} publish={handleStepSubmit} />,
    <ChoosePasswordStep
      stateStorage={userData}
      publish={handleStepSubmit}
      isLoading={isLoading}
    />,
  ];

  const renderStep = (stepIndex: number) => {
    return steps[stepIndex];
  };

  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((currentStep) => currentStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep((currentStep) => currentStep - 1);
    }
  };

  return (
    <Box className="relative flex flex-col flex-1 items-center text-center w-full">
      <ClientSideOnly>
        <Box className="mt-lg max-lg:w-full w-1/2 max-w-[600px]">
          <RegisterStepper currentStep={currentStep} />
        </Box>
      </ClientSideOnly>
      <Box className="flex flex-1 flex-col justify-start max-lg:w-full w-1/2 max-w-[600px]">
        <div className="relative flex items-center justify-center py-main">
          <IconButton
            id="register-button-stepback"
            className={`absolute left-0 text-primary ${
              currentStep === 0 ? 'hidden' : ''
            }`}
            onClick={goToPreviousStep}
          >
            <KeyboardBackspaceIcon />
          </IconButton>
          <Typography component="h1">Crie sua conta Vib</Typography>
        </div>
        {renderStep(currentStep)}
      </Box>
      <Typography component="h6" className="pt-main">
        Já possui cadastro?{' '}
        <Link id="register-link-login" href={urlDeLogin()}>
          Entrar agora
        </Link>
      </Typography>
    </Box>
  );
}

Register.getInitialProps = async ({ query }: NextPageContext) => {
  return { queryParams: query };
};

Register.pageType = PageTypes.PUBLIC;
Register.layout = Layouts.APPBARONLY;
/* Ver `PublicPageWrapper` em `_app`: sem isto o cadastro chega ao Google como um documento vazio. */
Register.renderizaSemSessao = true;
